import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { RotateCcw, Save } from 'lucide-react';
import { apiFetch } from '../../../../../shared/apiClient';
import { CustomSelect } from '../../../../../shared/components/CustomSelect';
import {
  PillarWeightsApi,
  DEFAULT_PILLAR_WEIGHTS,
  type PillarWeightsSettings,
  type PillarWeightsOverride,
} from '../../../../../features/analytics/pillarWeightsApi';

interface ProductInfo {
  id: string;
  name: string;
}

type PillarKey = 'commitment' | 'delivery_confidence' | 'capacity' | 'quality';

const PILLAR_KEYS: PillarKey[] = ['commitment', 'delivery_confidence', 'capacity', 'quality'];

const PILLAR_LABELS: Record<PillarKey, string> = {
  commitment: 'Commitment',
  delivery_confidence: 'Delivery Confidence',
  capacity: 'Capacity',
  quality: 'Quality',
};

const PILLAR_COLORS: Record<PillarKey, string> = {
  commitment: '#6366f1',
  delivery_confidence: '#8b5cf6',
  capacity: '#06b6d4',
  quality: '#22c55e',
};

const MIN_WEIGHT = 0;

function validate(weights: PillarWeightsSettings): string | null {
  for (const k of PILLAR_KEYS) {
    if (typeof weights[k] !== 'number' || !Number.isFinite(weights[k]))
      return `${PILLAR_LABELS[k]} must be a number`;
    if (weights[k] < MIN_WEIGHT) return `${PILLAR_LABELS[k]} must be >= ${MIN_WEIGHT}`;
    if (weights[k] > 100) return `${PILLAR_LABELS[k]} must be <= 100`;
  }
  const sum = PILLAR_KEYS.reduce((s, k) => s + weights[k], 0);
  if (Math.abs(sum - 100) > 0.01)
    return `Weights must sum to 100 (current: ${Math.round(sum * 100) / 100})`;
  return null;
}

// ── Interactive Stacked Bar ────────────────────────────────────────────────────

interface StackedBarProps {
  weights: PillarWeightsSettings;
  onChange: (weights: PillarWeightsSettings) => void;
}

const StackedBar: React.FC<StackedBarProps> = ({ weights, onChange }) => {
  const barRef = useRef<HTMLDivElement>(null);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);

  const total = PILLAR_KEYS.reduce((s, k) => s + weights[k], 0) || 100;

  const segments = PILLAR_KEYS.map((k) => ({
    key: k,
    value: weights[k],
    pct: (weights[k] / total) * 100,
    color: PILLAR_COLORS[k],
    label: PILLAR_LABELS[k],
  }));

  const getValueFromX = useCallback((clientX: number): number => {
    if (!barRef.current) return 0;
    const rect = barRef.current.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return Math.round(ratio * 100);
  }, []);

  useEffect(() => {
    if (draggingIndex === null) return;

    const handleMove = (clientX: number) => {
      const rawPct = getValueFromX(clientX);

      const next = { ...weights };
      // Boundary at `draggingIndex`: the divider between segment[draggingIndex-1] and segment[draggingIndex]
      // Compute cumulative % before this boundary
      let cumulativeBefore = 0;
      for (let i = 0; i < draggingIndex; i++) {
        cumulativeBefore += weights[PILLAR_KEYS[i]];
      }
      // This boundary's target position (as % of total)
      const boundaryTarget = rawPct;
      // The two pillars sharing this boundary
      const leftKey = PILLAR_KEYS[draggingIndex - 1];
      const rightKey = PILLAR_KEYS[draggingIndex];
      const leftVal = Math.max(MIN_WEIGHT, boundaryTarget - cumulativeBefore);
      const rightVal = Math.max(MIN_WEIGHT, weights[leftKey] + weights[rightKey] - leftVal);
      next[leftKey] = leftVal;
      next[rightKey] = rightVal;
      onChange(next);
    };

    const handleEnd = () => setDraggingIndex(null);

    const onMouseMove = (e: MouseEvent) => handleMove(e.clientX);
    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0) handleMove(e.touches[0].clientX);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', handleEnd);
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', handleEnd);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', handleEnd);
    };
  }, [draggingIndex, weights, onChange, getValueFromX]);

  // Compute cumulative offsets for divider handles
  let cumulative = 0;
  const dividers: { index: number; left: number }[] = [];
  for (let i = 0; i < segments.length - 1; i++) {
    cumulative += segments[i].pct;
    dividers.push({ index: i + 1, left: cumulative });
  }

  return (
    <div className="pillar-weights-bar" style={{ position: 'relative', marginTop: '8px' }}>
      <div
        ref={barRef}
        style={{
          display: 'flex',
          height: '44px',
          borderRadius: '10px',
          overflow: 'hidden',
          position: 'relative',
          border: '1px solid var(--glass-border, rgba(255,255,255,0.08))',
        }}
      >
        {segments.map((seg) => (
          <div
            key={seg.key}
            style={{
              width: `${seg.pct}%`,
              background: seg.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: '12px',
              fontWeight: 600,
              minWidth: '0',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              userSelect: 'none',
            }}
            title={`${seg.label}: ${seg.value}`}
          >
            {seg.pct >= 12 ? `${seg.value}` : ''}
          </div>
        ))}
      </div>

      {/* Draggable dividers */}
      {dividers.map((d) => (
        <div
          key={d.index}
          style={{
            position: 'absolute',
            top: '-4px',
            bottom: '-4px',
            left: `${d.left}%`,
            transform: 'translateX(-50%)',
            width: '12px',
            cursor: 'col-resize',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2,
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            setDraggingIndex(d.index);
          }}
          onTouchStart={(e) => {
            e.preventDefault();
            setDraggingIndex(d.index);
          }}
        >
          <div
            style={{
              width: '4px',
              height: '100%',
              borderRadius: '2px',
              background: 'rgba(255,255,255,0.5)',
              border: '1px solid rgba(0,0,0,0.15)',
            }}
          />
        </div>
      ))}

      {/* Legend below bar */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          marginTop: '8px',
          fontSize: '12px',
        }}
      >
        {segments.map((seg) => (
          <span
            key={seg.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              color: 'var(--color-text-secondary)',
            }}
          >
            <span
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '3px',
                background: seg.color,
                flexShrink: 0,
              }}
            />
            {seg.label}: <strong>{seg.value}</strong>
          </span>
        ))}
      </div>
    </div>
  );
};

// ── Main Panel Component ──────────────────────────────────────────────────────

export const PillarWeightsPanel: React.FC = () => {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const [products, setProducts] = useState<ProductInfo[]>([]);
  const [orgDefaults, setOrgDefaults] = useState<PillarWeightsSettings>({
    ...DEFAULT_PILLAR_WEIGHTS,
  });
  const [overrides, setOverrides] = useState<PillarWeightsOverride[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);

  const [localWeights, setLocalWeights] = useState<PillarWeightsSettings>({
    ...DEFAULT_PILLAR_WEIGHTS,
  });

  const currentSaved = useMemo(() => {
    if (!selectedProductId) return orgDefaults;
    const o = overrides.find((ov) => ov.productId === selectedProductId);
    return o?.settings ?? orgDefaults;
  }, [selectedProductId, orgDefaults, overrides]);

  const isOverride = useMemo(
    () => !!selectedProductId && overrides.some((o) => o.productId === selectedProductId),
    [selectedProductId, overrides]
  );

  const validationError = useMemo(() => validate(localWeights), [localWeights]);

  const hasChanges = useMemo(() => {
    return PILLAR_KEYS.some((k) => localWeights[k] !== currentSaved[k]);
  }, [localWeights, currentSaved]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [orgRes, overridesRes, productsRes] = await Promise.all([
          PillarWeightsApi.getOrgSettings(),
          PillarWeightsApi.getOverrides(),
          apiFetch('/api/products'),
        ]);
        if (cancelled) return;
        setOrgDefaults(orgRes.settings);
        setOverrides(overridesRes.overrides);
        if (productsRes.ok) {
          const payload = await productsRes.json();
          setProducts(
            (payload.products ?? []).map((p: ProductInfo) => ({ id: p.id, name: p.name }))
          );
        }
        setLocalWeights(orgRes.settings);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : 'Failed to load pillar weights');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const switchProduct = useCallback(
    (productId: string | null) => {
      setSelectedProductId(productId);
      setError(null);
      setSaved(false);
      if (!productId) {
        setLocalWeights(orgDefaults);
      } else {
        const o = overrides.find((ov) => ov.productId === productId);
        const s = o?.settings ?? orgDefaults;
        setLocalWeights(s);
      }
    },
    [orgDefaults, overrides]
  );

  const handleBarChange = useCallback((weights: PillarWeightsSettings) => {
    setLocalWeights(weights);
  }, []);

  const handleInputChange = useCallback((key: PillarKey, raw: string) => {
    const v = Number(raw);
    if (Number.isNaN(v)) return;
    setLocalWeights((prev) => ({ ...prev, [key]: v }));
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      if (!selectedProductId) {
        const res = await PillarWeightsApi.saveOrgSettings(localWeights);
        setOrgDefaults(res.settings);
      } else {
        await PillarWeightsApi.saveOverride(selectedProductId, localWeights);
        const updated = await PillarWeightsApi.getOverrides();
        setOverrides(updated.overrides);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      await queryClient.invalidateQueries({ queryKey: ['pillar-weights-effective'] });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save pillar weights');
    } finally {
      setSaving(false);
    }
  }, [selectedProductId, localWeights, queryClient]);

  const handleReset = useCallback(async () => {
    if (!selectedProductId) return;
    setSaving(true);
    setError(null);
    try {
      await PillarWeightsApi.deleteOverride(selectedProductId);
      const updated = await PillarWeightsApi.getOverrides();
      setOverrides(updated.overrides);
      setLocalWeights(orgDefaults);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      await queryClient.invalidateQueries({ queryKey: ['pillar-weights-effective'] });
    } catch {
      setError('Failed to reset pillar weights override');
    } finally {
      setSaving(false);
    }
  }, [selectedProductId, orgDefaults, queryClient]);

  const handleResetToDefaults = useCallback(() => {
    setLocalWeights({ ...DEFAULT_PILLAR_WEIGHTS });
  }, []);

  const sum = PILLAR_KEYS.reduce((s, k) => s + localWeights[k], 0);

  if (loading) {
    return (
      <div className="settings-panel">
        <div style={{ padding: '8px 0', color: 'var(--color-text-secondary)', fontSize: '13px' }}>
          Loading pillar weights...
        </div>
      </div>
    );
  }

  return (
    <div className="settings-panel">
      <div className="settings-panel__header">
        <h3 className="settings-panel__title">Delivery Pillars Thresholds</h3>
        <p className="settings-panel__description">
          Configure the weight distribution across the four delivery review pillars. Weights
          determine how much each pillar contributes to the overall delivery health score (max 100).
        </p>
      </div>

      {error && (
        <div className="settings-callout settings-callout--danger" style={{ marginBottom: '16px' }}>
          <div className="settings-callout__body">{error}</div>
        </div>
      )}

      {/* Product selector */}
      <div style={{ marginTop: '12px', marginBottom: '16px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px' }}>
          <span style={{ fontWeight: 500 }}>Editing settings for</span>
          <CustomSelect
            value={selectedProductId ?? ''}
            options={[
              { value: '', label: 'Organization default' },
              ...products.map((p) => ({
                value: p.id,
                label: `${p.name}${overrides.some((o) => o.productId === p.id) ? ' *' : ''}`,
              })),
            ]}
            onChange={(val) => switchProduct(val || null)}
            style={{ minWidth: '180px', fontSize: '14px' }}
          />
        </label>
        {selectedProductId && !isOverride && (
          <div
            style={{
              marginTop: '6px',
              fontSize: '12px',
              color: 'var(--color-text-secondary)',
            }}
          >
            Using org defaults. Edit and save to create a product-specific override.
          </div>
        )}
        {isOverride && (
          <div
            style={{
              marginTop: '6px',
              fontSize: '12px',
              color: 'var(--color-text-secondary)',
            }}
          >
            Product-specific override active.{' '}
            <span style={{ color: 'var(--color-text-muted)' }}>
              Org defaults: {orgDefaults.commitment}/{orgDefaults.delivery_confidence}/
              {orgDefaults.capacity}/{orgDefaults.quality}
            </span>
          </div>
        )}
      </div>

      {/* Stacked bar */}
      <StackedBar weights={localWeights} onChange={handleBarChange} />

      {/* Number inputs */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr 1fr',
          gap: '12px',
          marginTop: '16px',
        }}
      >
        {PILLAR_KEYS.map((k) => (
          <div key={k}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '13px',
                marginBottom: '4px',
              }}
            >
              <span
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '2px',
                  background: PILLAR_COLORS[k],
                  flexShrink: 0,
                }}
              />
              <span style={{ fontWeight: 500, color: 'var(--color-text)' }}>
                {PILLAR_LABELS[k]}
              </span>
            </label>
            <input
              type="number"
              min={MIN_WEIGHT}
              max={100}
              step={1}
              value={localWeights[k]}
              onChange={(e) => handleInputChange(k, e.target.value)}
              style={{
                width: '100%',
                padding: '6px 10px',
                borderRadius: '8px',
                border: '1px solid var(--glass-border)',
                background: 'var(--glass-bg)',
                fontSize: '14px',
                boxSizing: 'border-box',
              }}
            />
          </div>
        ))}
      </div>

      {/* Sum validation */}
      <div
        style={{
          marginTop: '8px',
          fontSize: '13px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span
          style={{
            color:
              Math.abs(sum - 100) < 0.01
                ? 'var(--color-success, #22c55e)'
                : 'var(--color-danger, #ef4444)',
          }}
        >
          Sum: {Math.round(sum * 100) / 100} / 100
        </span>
        {validationError && (
          <span style={{ color: 'var(--color-danger, #ef4444)', fontSize: '12px' }}>
            {validationError}
          </span>
        )}
      </div>

      {/* Info callout */}
      <div
        className="settings-callout"
        style={{
          marginTop: '16px',
          background: 'var(--glass-bg)',
          border: '1px solid var(--glass-border)',
          borderRadius: '10px',
          padding: '14px 16px',
          fontSize: '13px',
          lineHeight: 1.6,
          color: 'var(--color-text-secondary)',
        }}
      >
        <div
          style={{
            fontWeight: 600,
            color: 'var(--color-text-primary)',
            marginBottom: '6px',
          }}
        >
          How this works
        </div>
        <ul style={{ margin: 0, paddingLeft: '18px', listStyle: 'disc' }}>
          <li>Drag the boundaries on the stacked bar to redistribute weights.</li>
          <li>
            Or type exact values in the inputs above. The four weights must sum to exactly 100.
          </li>
          <li>
            Changes apply retroactively — historical trend data recalculates with current weights.
          </li>
          <li>Minimum per pillar: 0. Each point directly maps to the max score for that pillar.</li>
        </ul>
      </div>

      {/* Actions */}
      <div
        style={{
          marginTop: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '8px',
        }}
      >
        {!selectedProductId && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={saving || !hasChanges}
            onClick={handleResetToDefaults}
            title="Reset to hardcoded defaults"
          >
            <RotateCcw size={13} />
            Reset to defaults
          </button>
        )}
        {isOverride && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={saving}
            onClick={handleReset}
            title="Reset to org defaults"
          >
            <RotateCcw size={13} />
            {saving ? 'Resetting…' : 'Reset to org default'}
          </button>
        )}
        {saved && <span style={{ fontSize: '0.82rem', color: 'var(--color-success)' }}>Saved</span>}
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={!hasChanges || saving || !!validationError}
          onClick={handleSave}
        >
          <Save size={14} />
          {saving ? 'Saving…' : 'Save settings'}
        </button>
      </div>
    </div>
  );
};
